<script setup lang="ts">
// -------------------------------
// 🔹 Imports Vue et composables
// -------------------------------
import { toRaw, watchEffect } from 'vue';
import { useQuery } from '@tanstack/vue-query';

// -------------------------------
// 🔹 Imports composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useProductStore } from '@/store/product.store';
import { useDialogStore } from '@/store/dialog.store';

const dialogStore = useDialogStore();

// -------------------------------
// 🔹 Imports utilitaires
// -------------------------------

// -------------------------------
// 🔹 Imports assets / refs / données
// -------------------------------
import { arraysStepThree } from '@/refs/product.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';
import { useCategoryStore } from '@/store/category.store';
import BaseIncAndDecreMentBtn from '../common/BaseIncAndDecreMentBtn.vue';
import {
  horraire_commerce,
  isAtcivatedHorraireCommerceWatch,
  isUpatedHorraireCommerce,
} from '@/refs/horraire.commerce.refs';
import { idToDelete } from '@/refs/commerce.refs';

// -------------------------------
// 🔹 Imports Types
// -------------------------------

// -------------------------------
// 🔹 Props et emits instanciés
// -------------------------------

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const productStore = useProductStore();
const categoryStore = useCategoryStore();

// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

import { HorraireCommerceMutation } from '@/mutations/horraire.commerce.mutations';
import { isGlobalError } from '@/refs/common.refs';

// -------------------------------
// 🔹 Récupération des commandes avec Vue Query
// -------------------------------

// -------------------------------
// 🔹 Toutes les fonctions
// -------------------------------

// -------------------------------
// 🔹 Debugging (optionnel)
// -------------------------------
// watchEffect(() => {
//     console.log("Valeurs cochées :", productStore.datagetProduct);
// });

const { handleCreateHorraireCommerce, handleUpdateHorraireCommerce } =
  HorraireCommerceMutation();

const HandleInscrement = () => {
  horraire_commerce.value.push({
    type: '',
    weekday: '',
    day: '',
    open: '',
    close: '',
  });
};

const HandleDecreInscrement = (index?: number) => {
  if (horraire_commerce.value.length <= 1) return;
  horraire_commerce.value.splice(index, 1);
};
const formatDate = (date: Date) => {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const handleCreateHorraireCommerceFunc = () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const resp = handleCreateHorraireCommerce({
      schedules: horraire_commerce.value.map((i: any) => {
        return {
          type: i.type,
          weekday: i?.weekday === '' ? null : i?.weekday,
          day:
            i?.day && i?.weekday === 'special'
              ? formatDate(i.day)
              : formatDate(new Date()),
          open: i?.open,
          close: i?.close,
        };
      }),
    });

    if (resp) {
      setTimeout(() => {
        isAtcivatedHorraireCommerceWatch.value =
          !isAtcivatedHorraireCommerceWatch.value;
        isLoaderLoginFormActivated.value = false;
        dialogStore.closeDialog();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
      dialogStore.closeDialog();
    }, 2000);
  }
};

const handleUpdatedHorraireCommerceFunc = () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const i = horraire_commerce.value[0];
    const resp = handleUpdateHorraireCommerce({
      open: i?.open,
      close: i?.close,

      ...(i?.weekday === 'special'
        ? { day: i?.day ? formatDate(i.day) : null }
        : { weekday: i?.weekday || null }),
    });

    if (resp) {
      setTimeout(() => {
        isAtcivatedHorraireCommerceWatch.value =
          !isAtcivatedHorraireCommerceWatch.value;
        isLoaderLoginFormActivated.value = false;
        dialogStore.closeDialog();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
      dialogStore.closeDialog();
    }, 2000);
  }
};
const handleSubmit = () => {
  if (isUpatedHorraireCommerce.value) {
    handleUpdatedHorraireCommerceFunc();
  } else {
    handleCreateHorraireCommerceFunc();
  }
};
/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  idToDelete.value = null;
  emit('HandleClose');
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="w-full space-y-8">
      <div
        class="flex w-full flex-row items-start gap-4"
        v-for="(_sateIncrement, index) in horraire_commerce"
        :key="index"
      >
        <h1>{{ index + 1 }}</h1>
        <div class="w-full space-y-6">
          <div class="flex flex-col gap-2">
            <label
              for="taxe"
              class="text-14px font-tt-medium-Normal leading-normal"
            >
              Veuillez sélectionner le type d'horraire
              <span class="text-decroissance text-14px leading-normal">*</span>
            </label>

            <select
              required
              id="regular"
              name="regular"
              v-model="_sateIncrement.type"
              class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
            >
              <option value="" selected>Choisissez un type d'horraire</option>
              <option value="regular">Régulière</option>
              <option value="special">Spécial</option>
            </select>
          </div>

          <div
            class="flex flex-col gap-2"
            :class="_sateIncrement?.type === 'special' ? 'hidden' : 'block'"
          >
            <label
              for="taxe"
              class="text-14px font-tt-medium-Normal leading-normal"
            >
              Veuillez sélectionner le jour
              <span class="text-decroissance text-14px leading-normal">*</span>
            </label>

            <select
              :required="_sateIncrement?.type === 'special' ? false : true"
              id="weekday"
              name="weekday"
              v-model="_sateIncrement.weekday"
              class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
            >
              <option value="" selected>Choisissez le jour</option>
              <option value="monday">Lundi</option>
              <option value="tuesday">Mardi</option>
              <option value="wednesday">Mercredi</option>
              <option value="thursday">Jeudi</option>
              <option value="friday">Vendredi</option>
              <option value="saturday">Samedi</option>
              <option value="sunday">Dimanche</option>
            </select>
          </div>

          <TextInput
            :class="_sateIncrement?.type === 'special' ? 'block' : 'hidden'"
            :required="_sateIncrement?.type === 'special' ? true : false"
            v-model="_sateIncrement.day"
            name="Jour "
            placeholder="Valeur de la promotion"
            type="date"
            id="Valeur de la promotion"
            :label="`Selectioner une date `"
            class="flex-1"
          />
          <TextInput
            required="true"
            v-model="_sateIncrement.open"
            name=" Heure d’ouverture "
            placeholder=" Heure d’ouverture"
            type="time"
            id=" Heure d’ouverture"
            :label="` Heure d’ouverture `"
            class="flex-1"
          />
          <TextInput
            required="true"
            v-model="_sateIncrement.close"
            name="  Heure de fermeture "
            placeholder=" Heure de fermeture"
            type="time"
            id=" Heure de fermeture"
            :label="`  Heure de fermeture `"
            class="flex-1"
          />
        </div>
        <BaseIncAndDecreMentBtn
          @HandleDecreInscrement="() => HandleDecreInscrement(index)"
          :sateIncrement="index + 1"
          @HandleInscrement="() => HandleInscrement()"
        />
      </div>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="submit"
            :label="isUpatedHorraireCommerce ? 'Modifier' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
